import ApiDatabase from 'server';

/**
 * The calls of server/index.js the CRM pages compose (the record pages,
 * crmRecords.js), and the real lists they read (the companies, the groups),
 * kept a moment so that one page does not read them twice.
 */

// A call of server/index.js as a promise. Two reads of one endpoint at once
// would cancel each other: each gets its own `abortKey`.
export const ask = (method, payload, abortKey) =>
  new Promise((resolve, reject) =>
    abortKey ? ApiDatabase[method](payload, resolve, reject, { abortKey }) : ApiDatabase[method](payload, resolve, reject),
  );

const KEEP = 2 * 60 * 1000;
const kept = new Map();
// A real list, kept two minutes under `key`; a failed read is not kept.
const keep = (key, load) => {
  const now = Date.now();
  kept.forEach((entry, other) => now - entry.at >= KEEP && kept.delete(other));
  if (kept.has(key)) return kept.get(key).value;
  const value = load();
  kept.set(key, { at: now, value });
  value.catch(() => kept.delete(key));
  return value;
};

// A real list read again at its next use (after a write that changes it).
export const forget = (prefix) => kept.forEach((entry, key) => key.startsWith(prefix) && kept.delete(key));

// Every company of a search at once, for the record pages.
const ALL_COMPANIES = 5000;
export const realCompanies = (query = {}) =>
  keep(`companies:${JSON.stringify(query)}`, () =>
    ask('getCompanies', { ...query, page: 1, limit: ALL_COMPANIES }).then((data) => data?.companies ?? []),
  );

/**
 * The groups (big accounts) and their companies. The list of companies does
 * not say which group a company is in (its first $project drops
 * `bigAccounts`, api/useCases/admin/searchCompanies.usecase.js), so the
 * groups are read once and each company finds its own here.
 */
export const realGroups = () =>
  keep('groups', () =>
    ask('getBigAccounts', { search: '' }).then((data) =>
      (data?.bigAccounts ?? []).map((group) => ({
        id: String(group._id),
        name: group.name,
        companyIds: (group.companies ?? []).map((link) => String(link.id_company)),
      })),
    ),
  );

// The group of each company: { [companyId]: { id, name } }.
export const bigAccountsById = () =>
  realGroups().then((groups) =>
    Object.fromEntries(groups.flatMap((group) => group.companyIds.map((id) => [id, { id: group.id, name: group.name }]))),
  );
